import React, { useEffect, useMemo, useState } from 'react';
import {
	MedicalExaminationState,
	useDentUpcomingMedicalExaminationsQuery,
	type DentUpcomingMedicalExaminationsQuery,
} from '../../../../graphql/graphql';
import type { QueryDataItem } from '../../../../Interfaces/QueryDataItem';
import { useDENTContext } from '../../Services/DENTContext';
import DENTMedicalExaminationsAlertModal from './DENTMedicalExaminationsAlertModal';
import { UpcomingExaminationEvent } from '../../Interfaces/medicalExamination/UpcomingExaminationEvent.interface';

type UpcomingDentMedicalExaminationRow = QueryDataItem<
	NonNullable<DentUpcomingMedicalExaminationsQuery['medicalExaminations']>
>;

const DENTUpcomingMedicalExaminations = () => {
	const { patientId, activeMedicalExaminationId } = useDENTContext();
	const [userDismissed, setUserDismissed] = useState(false);

	useEffect(() => {
		if (activeMedicalExaminationId) {
			setUserDismissed(true);
		}
	}, [patientId, activeMedicalExaminationId]);

	const { data, isLoading } = useDentUpcomingMedicalExaminationsQuery(
		{
			where: {
				patientId: patientId!,
				state: MedicalExaminationState.EventAroundNow,
			},
			pagination: { take: 20 },
		},
		{ skip: !patientId || !!activeMedicalExaminationId },
	);

	const events: UpcomingExaminationEvent[] = useMemo(() => {
		const rows = data?.medicalExaminations?.data ?? [];
		return rows
			.filter((exam: UpcomingDentMedicalExaminationRow) => exam.event?.id)
			.map((exam: UpcomingDentMedicalExaminationRow) => ({
				id: exam.event!.id,
				dateFrom: exam.event!.dateFrom,
				dateTo: exam.event!.dateTo,
				title: exam.event!.title ?? undefined,
				description: exam.event!.description ?? undefined,
				patientName: exam.patient?.profile?.fullName ?? undefined,
				extra: { medicalExaminationId: exam.id },
			}));
	}, [data]);

	if (
		isLoading ||
		events.length === 0 ||
		activeMedicalExaminationId ||
		userDismissed
	) {
		return <></>;
	}

	return (
		<DENTMedicalExaminationsAlertModal
			isOpen
			events={events}
			onClose={() => setUserDismissed(true)}
		/>
	);
};

export default DENTUpcomingMedicalExaminations;
